import { useMemo } from 'react';

/**
 * Memoizes data based on a stable key, ensuring the reference only changes
 * when the key content changes, not when the data reference changes.
 *
 * @param data - The data to memoize
 * @param getKey - Optional function to extract a comparable key from the data.
 *                 If not provided, uses JSON.stringify on the data itself.
 * @returns The memoized data with stable reference
 *
 * @example
 * Simple usage - memoize based on JSON.stringify
 * const stableData = useStableReference(apiData.data);
 *
 * @example
 * Custom key extraction
 * const stableData = useStableReference(
 *   metadataApi.data,
 *   (data) => JSON.stringify(data?.map(item => item.value) ?? [])
 * );
 */
export function useStableReference<T>(
  data: T,
  getKey?: (data: T) => string
): T {
  const key = useMemo(() => {
    if (data == null) return '';
    return getKey ? getKey(data) : JSON.stringify(data);
  }, [data, getKey]);

  return useMemo(() => data, [key]);
}
